import React, { memo } from 'react'
import { Card } from 'transfix-ui/components/Card'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { getCostStrategy } from 'apps/marketplace/pages/spot-quote-tool/utils/getCostStrategy'
import { ISpotRate } from 'apps/marketplace/store/amQuoting/types'
import { COST_STRATEGY } from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import { formatRate } from 'utils/formatRate'
import { CostCalTableRow } from './CostCalTableRow'
import { TcmCard } from './TcmCard'

interface ICostReferenceCardProps {
  spotRate: Partial<ISpotRate> & {
    metadata: ISpotRate['metadata']
    rates: ISpotRate['rates']
    priceRequestId: ISpotRate['priceRequestId']
  }
}

const MULTI_STOP_BUCKETS = [
  'Standard Multi-stop',
  'Local Multi-stop',
  'Long Haul Direct & Multi-stop',
  'Long Haul Multi-stop, Short Direct ',
  'True Round trip',
] as const

const useStyles = makeStyles(() => ({
  cardContainer: {
    height: '100%',
  },
  fontColor: {
    color: brandColors.coolGray5,
  },
}))

export const CostReferenceCard = memo<ICostReferenceCardProps>(({ spotRate }) => {
  const {
    metadata: {
      additionalStops,
      adjustedCostBasis,
      appliedCostControlConfig,
      datType,
      dsCostPremiumPct,
      multiStopBucketUsed,
      strategyChosen,
    },
    rates: {
      allIn,
      datEstimatedAllIn,
      datHighAllIn,
      datLowAllIn,
      transfixCostPrediction40Percent,
      transfixCostPredictionBottom,
      transfixCostPredictionEstimate,
      transfixCostPredictionTop,
    },
  } = spotRate

  const classes = useStyles()
  const formatRatioPercentage = (
    numerator: number,
    denominator: number,
    numDecimals: number = 2
  ) => {
    const ratio = ((numerator / denominator) * 100).toFixed(numDecimals)
    return `${ratio}%`
  }
  const isAdditionalStop = !!additionalStops && additionalStops.length > 0
  const syntheticDat = 'Synthetic Dat Rate Being Used'

  const displayAdjustedCostBasis =
    getCostStrategy(strategyChosen, dsCostPremiumPct) !==
    COST_STRATEGY.defaultSpotControlPanelConfigs
      ? adjustedCostBasis
      : undefined

  return (
    <Box p={3}>
      <div className='pb3'>
        <Typography variant='subtitle2'>Cost Reference</Typography>
      </div>
      <Grid container direction='row' spacing={2} className='pt3'>
        <Grid item xs={3} lg={isAdditionalStop ? 4 : 6}>
          <Card className={classes.cardContainer}>
            <Typography variant='overline'>DAT</Typography>
            {datType === 'synthetic_rate' && (
              <Tag
                label={syntheticDat}
                className='ml2'
                size={TAG_SIZE.xsmall}
                tagStyle={TAG_STYLE.info}
              />
            )}
            <table className='table'>
              <tbody>
                <CostCalTableRow
                  label='Dat'
                  value={formatRate(datEstimatedAllIn)}
                  stops={isAdditionalStop}
                  labelClass={classes.fontColor}
                />
                <CostCalTableRow
                  label='All-In to Dat %'
                  value={formatRatioPercentage(allIn, datEstimatedAllIn)}
                  stops={isAdditionalStop}
                  labelClass={classes.fontColor}
                />
                <CostCalTableRow
                  label='High Dat'
                  value={formatRate(datHighAllIn)}
                  stops={isAdditionalStop}
                  labelClass={classes.fontColor}
                />
                <CostCalTableRow
                  label='Low Dat'
                  value={formatRate(datLowAllIn)}
                  stops={isAdditionalStop}
                  labelClass={classes.fontColor}
                />
              </tbody>
            </table>
          </Card>
        </Grid>
        <Grid item xs={3} lg={isAdditionalStop ? 4 : 6}>
          <TcmCard
            appliedCostControlConfig={appliedCostControlConfig}
            tcm25={transfixCostPredictionBottom}
            allIn={allIn}
            tcm75={transfixCostPredictionTop}
            tcm40={transfixCostPrediction40Percent}
            tcm50={transfixCostPredictionEstimate}
            additionalStops={additionalStops}
            adjustedCostBasis={displayAdjustedCostBasis}
          />
        </Grid>
        {isAdditionalStop && (
          <Grid item xs={3} lg={4}>
            <Card className={classes.cardContainer}>
              <Typography variant='overline'>MULTI STOP</Typography>
              <table className='table'>
                <tbody>
                  <CostCalTableRow
                    label='Cost Method Used'
                    value={MULTI_STOP_BUCKETS[multiStopBucketUsed - 1]}
                    multistop={isAdditionalStop}
                  />
                  <CostCalTableRow
                    label='Cost Estimate'
                    value={formatRate(datEstimatedAllIn)}
                    multistop={isAdditionalStop}
                  />
                </tbody>
              </table>
              <div>
                <a
                  href='https://transfix.atlassian.net/wiki/spaces/SUG/pages/2721284116/Multi-Stop+Spot+Bidding'
                  target='_blank'
                  rel='noopener noreferrer'>
                  Learn more in the wiki
                </a>
              </div>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
})
